import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useStore } from "zustand";
import { usePip } from "@/hooks/use-pip";
import { authStore } from "@/lib/stores/auth-store";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";
import { PipCombinedContent } from "./pip-combined-content";

const PIP_WIDTH_SINGLE = 320;
const PIP_HEIGHT_SINGLE = 240;
const PIP_WIDTH_COMBINED = 400;
const PIP_HEIGHT_COMBINED = 480;

export function FloatingPipPanel() {
	const user = useStore(authStore, (state) => state.user);
	const activeChannel = useStore(uiStore, (state) => state.activeChannel);
	const room = useStore(uiStore, (state) => state.room);
	const broadcast = useStore(coreStore, (state) => state.broadcast);
	const broadcastRoom = useStore(uiStore, (state) => state.broadcastRoom);
	const navigate = useStore(uiStore, (state) => state.navigate);
	const gridItems = useStore(uiStore, (state) => state.gridItems);

	const hasCall = activeChannel && room;
	const hasBroadcast =
		broadcast && broadcastRoom && broadcast.status === "live";
	const bothActive = hasCall && hasBroadcast;

	const activeTabType =
		gridItems.length > 0
			? gridItems[0]?.tabs[gridItems[0]?.activeTabIndex]?.type
			: null;
	const isViewingMediaTab =
		activeTabType === "voiceChannel" || activeTabType === "broadcastControls";

	const prevIsViewingMediaTab = useRef(isViewingMediaTab);
	const pipWidth = bothActive ? PIP_WIDTH_COMBINED : PIP_WIDTH_SINGLE;
	const pipHeight = bothActive ? PIP_HEIGHT_COMBINED : PIP_HEIGHT_SINGLE;

	const handlePipClose = useCallback(() => {
		if (!(hasCall || hasBroadcast)) {
			return;
		}

		let targetType: "voiceChannel" | "broadcastControls" = "voiceChannel";
		if (!hasCall && hasBroadcast && user?.uid === broadcast?.startedBy) {
			targetType = "broadcastControls";
		}

		const currentGridItems = uiStore.getState().gridItems;
		const firstItem = currentGridItems[0];

		if (firstItem) {
			const existingTabIndex = firstItem.tabs.findIndex(
				(tab) => tab.type === targetType
			);
			if (existingTabIndex !== -1) {
				uiStore.getState().setActiveTab(firstItem.id, existingTabIndex);
				return;
			}
		}

		// No such tab, so the voice or broadcast tab was removed. Open a new one.
		navigate({ type: targetType }, { newTab: true });
	}, [hasCall, hasBroadcast, navigate, user?.uid, broadcast?.startedBy]);

	const setPipOpenFn = useStore(uiStore, (state) => state.setPipOpenFn);

	const { isPipActive, pipWindow, openPip, closePip } = usePip({
		width: pipWidth,
		height: pipHeight,
		onClose: handlePipClose,
	});

	useEffect(() => {
		setPipOpenFn(openPip);
		return () => setPipOpenFn(null);
	}, [openPip, setPipOpenFn]);

	const hasAnyMedia = hasCall || hasBroadcast;

	useEffect(() => {
		if (!hasAnyMedia && isPipActive) {
			closePip();
			return;
		}

		const wasViewingMedia = prevIsViewingMediaTab.current;
		prevIsViewingMediaTab.current = isViewingMediaTab;

		if (!wasViewingMedia && isViewingMediaTab && isPipActive) {
			closePip();
		}
	}, [hasAnyMedia, isPipActive, closePip, isViewingMediaTab]);

	const handleGoBack = useCallback(() => {
		handlePipClose();
		closePip();
	}, [handlePipClose, closePip]);

	if (!(hasAnyMedia && isPipActive && pipWindow) || pipWindow?.closed) {
		return null;
	}

	return createPortal(
		<PipCombinedContent
			onClose={handleGoBack}
			onGoBack={handleGoBack}
			showBroadcast={!!hasBroadcast}
			showCall={!!hasCall}
		/>,
		pipWindow.document.body
	);
}
